/**
 * BaseDrawerClass - Regular ES6 class for drawer components
 *
 * Provides common drawer functionality without requiring Web Components.
 * Designed for existing drawer classes to extend.
 *
 * Features:
 * - Open/close state management with smooth animations
 * - Keyboard navigation (ESC key)
 * - Backdrop click detection
 * - Body scroll lock
 * - Shopify chat visibility toggle
 * - Custom event emission
 *
 * Usage:
 * class CartDrawer extends BaseDrawerClass {
 *     constructor() {
 *         super();
 *         this.drawer = document.querySelector('.custom-cart-drawer');
 *         this.initDrawer();
 *     }
 * }
 */
class BaseDrawerClass {
    constructor() {
        // State management
        this.isDrawerOpen = false;
        this.previouslyFocusedElement = null;

        // Elements (to be set by subclass)
        this.drawer = null;
        this.drawerContent = null;
        this.closeButton = null;
        this.overlay = null;

        // Default options (can be overridden in subclasses)
        this.drawerOptions = {
            closeOnBackdrop: true,
            closeOnEscape: true,
            lockBodyScroll: true,
            hideShopifyChat: true,
            animationDuration: 300 // ms (should match CSS transition)
        };

        // Bind methods to preserve context
        this.handleEscape = this.handleEscape.bind(this);
        this.handleBackdropClick = this.handleBackdropClick.bind(this);
    }

    /**
     * Initialize drawer - Call this after setting this.drawer in subclass constructor
     */
    initDrawer() {
        if (!this.drawer) {
            window.debugLogger.error('BaseDrawerClass: drawer element not set');
            return;
        }

        this.setupDrawerEventListeners();
    }

    /**
     * Setup drawer event listeners
     */
    setupDrawerEventListeners() {
        // ESC key to close
        if (this.drawerOptions.closeOnEscape) {
            document.addEventListener('keydown', this.handleEscape);
        }

        // Backdrop click (if drawer itself is the backdrop)
        if (this.drawerOptions.closeOnBackdrop) {
            this.drawer.addEventListener('click', this.handleBackdropClick);
        }

        // Close button (if set)
        if (this.closeButton) {
            this.closeButton.addEventListener('click', () => this.closeDrawer());
        }

        // Prevent drawer from closing when clicking inside content
        if (this.drawerContent) {
            this.drawerContent.addEventListener('click', (event) => {
                event.stopPropagation();
            });
        }
    }

    /**
     * Handle ESC key press
     */
    handleEscape(event) {
        if (event.key === 'Escape' && this.isDrawerOpen) {
            this.closeDrawer();
        }
    }

    /**
     * Handle backdrop click
     */
    handleBackdropClick(event) {
        // If click is inside content, don't close
        if (this.drawerContent && this.drawerContent.contains(event.target)) {
            return;
        }

        // Click on drawer itself (backdrop), close it
        this.closeDrawer();
    }

    /**
     * Open the drawer
     */
    openDrawer() {
        if (this.isDrawerOpen) return;

        // Store currently focused element
        this.previouslyFocusedElement = document.activeElement;

        // Remove closing class if it exists
        this.drawer.classList.remove('closing');

        // Force reflow for animation
        void this.drawer.offsetWidth;

        // Add active class
        this.drawer.classList.add('active');

        // Lock body scroll
        if (this.drawerOptions.lockBodyScroll) {
            document.body.classList.add('overflow-hidden');
        }

        // Hide Shopify chat
        if (this.drawerOptions.hideShopifyChat) {
            this.toggleShopifyChat(false);
        }

        this.isDrawerOpen = true;

        // Dispatch event
        this.dispatchDrawerEvent('drawer:open');

        // Call lifecycle hook (can be overridden in subclass)
        if (this.onDrawerOpen) {
            this.onDrawerOpen();
        }
    }

    /**
     * Close the drawer
     */
    closeDrawer() {
        if (!this.isDrawerOpen) return;

        // Add closing class for animation
        this.drawer.classList.add('closing');

        // Wait for animation
        setTimeout(() => {
            this.drawer.classList.remove('active', 'closing');

            // Unlock body scroll
            if (this.drawerOptions.lockBodyScroll) {
                document.body.classList.remove('overflow-hidden');
            }

            // Show Shopify chat
            if (this.drawerOptions.hideShopifyChat) {
                this.toggleShopifyChat(true);
            }

            this.isDrawerOpen = false;

            // Restore focus
            if (this.previouslyFocusedElement && this.previouslyFocusedElement.focus) {
                this.previouslyFocusedElement.focus();
                this.previouslyFocusedElement = null;
            }

            // Dispatch event
            this.dispatchDrawerEvent('drawer:close');

            // Call lifecycle hook (can be overridden in subclass)
            if (this.onDrawerClose) {
                this.onDrawerClose();
            }
        }, this.drawerOptions.animationDuration);
    }

    /**
     * Toggle drawer state
     */
    toggleDrawer() {
        return this.isDrawerOpen ? this.closeDrawer() : this.openDrawer();
    }

    /**
     * Check if drawer is open
     */
    isDrawerCurrentlyOpen() {
        return this.isDrawerOpen;
    }

    /**
     * Toggle Shopify chat visibility
     */
    toggleShopifyChat(visible) {
        const shopifyChat = document.getElementById('shopify-chat');
        if (shopifyChat) {
            shopifyChat.style.visibility = visible ? 'visible' : 'hidden';
            shopifyChat.style.opacity = visible ? '1' : '0';
        }
    }

    /**
     * Dispatch custom event
     */
    dispatchDrawerEvent(eventName, detail = {}) {
        const event = new CustomEvent(eventName, {
            bubbles: true,
            cancelable: true,
            detail: {
                drawer: this,
                ...detail
            }
        });

        if (this.drawer) {
            this.drawer.dispatchEvent(event);
        }
    }

    /**
     * Cleanup - Call when destroying drawer instance
     */
    destroyDrawer() {
        document.removeEventListener('keydown', this.handleEscape);

        if (this.drawer) {
            this.drawer.removeEventListener('click', this.handleBackdropClick);
        }

        if (this.isDrawerOpen) {
            this.closeDrawer();
        }
    }
}

// Make available globally
window.BaseDrawerClass = BaseDrawerClass;
